/**
 * UGC 화면(ADR-048) — 타이틀 패널 안에서 교체되는 두 화면(투고 · 인기)과, 게임오버 패널의 별 줄.
 * 새 화면 id 없이 `#overlay` 의 현재 패널을 갈아 끼운다(화면 관리자는 한 번에 하나만 연다 · UPLIFT §3.1).
 * 정답은 인기 목록에 절대 나오지 않는다 — 별은 판을 치른 뒤 그 판의 문제에만 준다.
 */
import type { Locale } from '../engine/alphabet';
import { t } from '../i18n';
import {
  type TopJoke,
  type UgcClient,
  UgcError,
  type UgcErrorKind,
  type UgcSubmission,
  prepareSubmission,
} from '../ugc/client';

export interface UgcDeps {
  readonly client: UgcClient;
  readonly uid: string;
  readonly locale: Locale;
  readonly toast: (text: string) => void;
  /** 주간 인기 팩이 발행돼 있으면 그 팩으로 판을 시작하는 함수(없으면 null) */
  readonly playWeekly: (() => void) | null;
}

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag);
  if (cls !== undefined) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
};

const statusLabel = (s: UgcSubmission['status']): string =>
  s === 'approved' ? t().ugc.approved : s === 'rejected' ? t().ugc.rejected : t().ugc.pending;

/** 투고 실패 → 사람 문구(RULES §1.19 UGC_AVAILABILITY). HTTP 상태 · 서비스 원문은 보이지 않는다 */
export function ugcErrorText(e: unknown): string {
  const S = t().ugc;
  const text: Readonly<Record<UgcErrorKind, string>> = {
    offline: S.errOffline,
    unavailable: S.unavailable,
    invalid: S.errInvalid,
    duplicate: S.errDuplicate,
    limit: S.errLimit,
  };
  return text[e instanceof UgcError ? e.kind : 'unavailable'];
}

/**
 * 투고 화면 — 입력할 때마다 규칙 4~14 로 지적하고, 통과해야 보내기가 켜진다.
 * 열 때 서비스를 먼저 확인하고(식별자 없음 · 짧게) 받을 수 없으면 입력칸 없이 안내만 한다(RULES §1.19 UGC_AVAILABILITY) —
 * 게임 정적 파일만 먼저 배포된 상태에서 개그를 다 쓴 뒤에야 실패를 보이지 않게.
 */
export function renderUgcSubmit(root: HTMLElement, deps: UgcDeps, back: () => void): void {
  const S = t().ugc;
  const panel = el('div', 'panel ugc-panel');
  panel.append(el('h1', '', S.submitTitle));
  panel.append(el('p', 'title-hint', S.submitHint));
  const state = el('p', 'ugc-state', S.loading);

  const q = el('input');
  q.placeholder = S.qPlaceholder;
  q.maxLength = 40;
  const a = el('input');
  a.placeholder = S.aPlaceholder;
  a.maxLength = 5;
  // 서비스 확인이 끝나기 전에는 잠근다
  q.disabled = true;
  a.disabled = true;
  const issues = el('ul', 'ugc-issues');
  const send = el('button', 'primary', S.send);
  send.disabled = true;
  let prepared = prepareSubmission('', '', deps.locale);

  const refresh = (): void => {
    prepared = prepareSubmission(q.value, a.value, deps.locale);
    issues.replaceChildren();
    const shown = prepared.issues.filter((i) => i.rule !== 8 && i.rule !== 3); // 오답은 자동 · id 는 임시
    for (const i of shown.slice(0, 3)) issues.append(el('li', '', i.message));
    send.disabled = q.value.trim() === '' || a.value.trim() === '' || prepared.issues.length > 0;
  };
  q.addEventListener('input', refresh);
  a.addEventListener('input', refresh);

  send.addEventListener('click', () => {
    if (prepared.issues.length > 0) return;
    send.disabled = true;
    void deps.client
      .submit(deps.uid, deps.locale, prepared.q, prepared.a, prepared.d)
      .then(() => {
        deps.toast(S.sent);
        q.value = '';
        a.value = '';
        refresh();
        return loadMine();
      })
      .catch((e: unknown) => {
        deps.toast(ugcErrorText(e));
        send.disabled = false;
      });
  });

  const mine = el('ul', 'ugc-list');
  const loadMine = async (): Promise<void> => {
    try {
      const list = await deps.client.mine(deps.uid);
      mine.replaceChildren();
      if (list.length > 0) mine.append(el('li', 'ugc-head', S.myTitle));
      for (const s of list) {
        const li = el('li');
        li.append(el('span', '', s.q));
        li.append(el('span', `ugc-status ${s.status}`, statusLabel(s.status)));
        mine.append(li);
      }
    } catch {
      // 목록은 있으면 좋은 것 — 실패해도 투고는 된다
    }
  };

  const close = el('button', 'secondary', S.back);
  close.addEventListener('click', back);
  panel.append(state, q, a, issues, send, mine, close);
  root.replaceChildren(panel);
  void deps.client.available(deps.locale).then((ok) => {
    if (!panel.isConnected) return; // 확인 중에 돌아갔다
    if (!ok) {
      state.textContent = S.unavailable;
      for (const n of [q, a, issues, send]) n.remove();
      return;
    }
    state.remove();
    q.disabled = false;
    a.disabled = false;
    q.focus();
    void loadMine();
  });
}

/** 인기 개그 — 문제문과 별 수만. 정답은 판에서 */
export function renderUgcTop(root: HTMLElement, deps: UgcDeps, back: () => void): void {
  const S = t().ugc;
  const panel = el('div', 'panel ugc-panel');
  panel.append(el('h1', '', S.topTitle));
  panel.append(el('p', 'title-hint', S.topNote));
  const list = el('ol', 'ugc-list');
  list.append(el('li', 'ugc-head', S.loading));
  panel.append(list);
  if (deps.playWeekly !== null) {
    const play = el('button', 'primary', S.playWeekly);
    const go = deps.playWeekly;
    play.addEventListener('click', go);
    panel.append(play);
  }
  const close = el('button', 'secondary', S.back);
  close.addEventListener('click', back);
  panel.append(close);
  root.replaceChildren(panel);

  void deps.client
    .top(deps.locale, deps.uid, 30)
    .then((rows: TopJoke[]) => {
      list.replaceChildren();
      if (rows.length === 0) list.append(el('li', 'ugc-head', S.empty));
      for (const r of rows) {
        const li = el('li');
        li.append(el('span', '', r.q));
        li.append(el('span', `ugc-votes${r.mine ? ' mine' : ''}`, `★ ${r.votes}`));
        list.append(li);
      }
    })
    .catch(() => {
      list.replaceChildren(el('li', 'ugc-head', S.offline));
    });
}

export interface StarItem {
  readonly slot: string; // c001…
  readonly q: string;
}

/** 게임오버 패널의 별 줄 — 이 판에 나온 문제에만. 토글은 서비스에 uid 당 하나로 남는다 */
export function mountStarRow(
  panel: Element,
  items: readonly StarItem[],
  toggle: (slot: string, on: boolean) => Promise<number>,
): void {
  const S = t().ugc;
  const box = el('div', 'ugc-stars');
  box.append(el('p', 'ugc-head', S.starTitle));
  const state = new Map<string, boolean>();
  for (const it of items) {
    const row = el('button', 'ugc-star');
    row.type = 'button';
    const star = el('span', 'ugc-star-mark', '☆');
    const text = el('span', '', it.q.length > 26 ? `${it.q.slice(0, 25)}…` : it.q);
    row.append(star, text);
    row.addEventListener('click', () => {
      const on = !(state.get(it.slot) ?? false);
      row.disabled = true;
      void toggle(it.slot, on)
        .then((votes) => {
          state.set(it.slot, on);
          star.textContent = on ? `★ ${votes}` : '☆';
          row.classList.toggle('on', on);
        })
        .catch(() => {
          star.textContent = '☆';
        })
        .finally(() => {
          row.disabled = false;
        });
    });
    box.append(row);
  }
  panel.append(box);
}
